package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.colaboradores.DatasColab
import br.com.htms.inspeccaocampo.data.funcionarios.Cpf
import br.com.htms.inspeccaocampo.platform.PlatformServices
import kotlin.math.ceil

/**
 * Modelos de certificado. Os fundos `cert_<chave>_p1/p2.jpg` vêm dos PDFs originais já sem o texto
 * variável e sem a assinatura do aluno, com a logo atual (gerados por `_icd_build/cert/prep_cert.py`).
 * Coordenadas medidas do topo da página.
 */
enum class CertificadoModelo(
    val rotulo: String,
    val chave: String,
    val curso: String,
    val cargaHoraria: String,
    val norma: String,
    val cidadePadrao: String,
    internal val topoParagrafo: Double,
    internal val xCidade: Double,
    internal val topoCidade: Double,
    internal val nomeXa: Double,
    internal val nomeXb: Double,
    internal val baseNome: Double,
    /** Fundo sem linha/rótulo do aluno (no modelo eram parte da imagem da assinatura): desenha com o rótulo padrão. */
    internal val desenharLinhaAluno: Boolean = false
) {
    NR06(
        "NR 06 - EPI", "nr06",
        "“Curso de NR 06 – Equipamento de Proteção Individual”", "04 horas", "06", "Curitiba - PR",
        topoParagrafo = 236.0, xCidade = 47.0, topoCidade = 378.0, nomeXa = 58.0, nomeXb = 329.0, baseNome = 492.0
    ),
    NR10_BASICO(
        "NR 10 - Básico", "nr10basico",
        "“Curso de NR-10 Segurança em Instalações e Serviços de Eletricidade BÁSICO”", "40 horas", "10", "Cascavel - PR",
        topoParagrafo = 211.0, xCidade = 63.0, topoCidade = 328.0, nomeXa = 235.0, nomeXb = 480.0, baseNome = 484.0
    ),
    NR10_SEP(
        "NR 10 - SEP", "nr10sep",
        "“Curso de NR-10 Segurança em Instalações e Serviços de Eletricidade SEP”", "40 horas", "10", "Cascavel - PR",
        topoParagrafo = 228.0, xCidade = 52.0, topoCidade = 347.0, nomeXa = 235.0, nomeXb = 480.0, baseNome = 484.0
    ),
    NR18(
        "NR 18 - Construção Civil", "nr18",
        "“Curso de NR 18 – Condições e Meio Ambiente de Trabalho na Indústria da Construção”", "06 horas", "18",
        "Curitiba - PR",
        topoParagrafo = 237.0, xCidade = 64.0, topoCidade = 369.0, nomeXa = 49.0, nomeXb = 319.0, baseNome = 495.0
    ),
    NR18_MUNCK(
        "NR 18 - Guindaste e Munck", "nr18munck",
        "“Curso de NR-18 Operação de guindaste e guindauto (Munck) Teoria e prática”", "120 horas", "18",
        "Curitiba - PR",
        topoParagrafo = 200.0, xCidade = 34.0, topoCidade = 339.0, nomeXa = 243.0, nomeXb = 452.0, baseNome = 489.0,
        desenharLinhaAluno = true
    ),
    NR33(
        "NR 33 - Trabalhador Autorizado e Vigia", "nr33",
        "“Curso de NR-33 - TREINAMENTO DE CAPACITAÇÃO PARA TRABALHADOR AUTORIZADO E VIGIA NOS TRABALHOS EM " +
            "ESPAÇOS CONFINADOS”",
        "16 horas", "33", "Curitiba - PR",
        topoParagrafo = 222.0, xCidade = 46.0, topoCidade = 357.0, nomeXa = 84.0, nomeXb = 281.0, baseNome = 487.0
    ),
    NR33_SUPERVISOR(
        "NR 33 - Supervisor de Entrada", "nr33sup",
        "“Curso de NR-33 - TREINAMENTO DE CAPACITAÇÃO PARA SUPERVISOR DE ENTRADA NOS TRABALHOS EM " +
            "ESPAÇOS CONFINADOS”",
        "40 horas", "33", "Curitiba - PR",
        topoParagrafo = 222.0, xCidade = 45.0, topoCidade = 380.0, nomeXa = 74.0, nomeXb = 306.0, baseNome = 487.0
    ),
    NR35(
        "NR 35 - Trabalho em Altura", "nr35",
        "“Curso de NR 35 - Trabalho em Altura”", "16 horas", "35", "Curitiba - PR",
        topoParagrafo = 228.0, xCidade = 47.0, topoCidade = 377.0, nomeXa = 55.0, nomeXb = 326.0, baseNome = 497.0
    ),
    NR35_RESGATE(
        "NR 35 - Resgate em Altura", "nr35resgate",
        "“Resgate em Altura (torres de energia e linhas de transmissão)”", "16 horas", "35", "Curitiba - PR",
        topoParagrafo = 228.0, xCidade = 21.0, topoCidade = 345.0, nomeXa = 8.0, nomeXb = 236.0, baseNome = 472.0
    );

    val assetPagina1: String get() = "cert_${chave}_p1.jpg"
    val assetPagina2: String get() = "cert_${chave}_p2.jpg"
}

data class CertificadoDados(
    val modelo: CertificadoModelo,
    val nome: String,
    val cpf: String,
    /** dd/MM/yyyy */
    val inicio: String,
    val fim: String,
    val dataEmissao: String,
    val cidade: String
)

object CertificadoPdfBuilder {
    private const val W = 842.0
    private const val H = 595.0
    private const val X0 = 48.0
    private const val X1 = W - 48.0
    private const val ROTULO_ALUNO = "Aluno"
    private const val EMPRESA = "HTMS ENERGIA LTDA – CNPJ: 51.475.783/0001-09"

    private val MESES = listOf(
        "janeiro", "fevereiro", "março", "abril", "maio", "junho",
        "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
    )

    private data class Data(val dia: Int, val mes: Int, val ano: Int)

    private fun ler(s: String): Data? {
        val p = s.trim().split('/')
        if (p.size != 3) return null
        val d = p[0].toIntOrNull() ?: return null
        val m = p[1].toIntOrNull() ?: return null
        val a = p[2].toIntOrNull() ?: return null
        return if (d in 1..31 && m in 1..12 && a in 1900..2200) Data(d, m, a) else null
    }

    private fun mes(d: Data) = MESES[d.mes - 1]

    fun dataPorExtenso(s: String): String = ler(s)?.let { "${it.dia} de ${mes(it)} de ${it.ano}" } ?: s.trim()

    private fun ordinal(d: Data) = d.ano * 400 + d.mes * 32 + d.dia

    private fun consecutivos(a: Data, b: Data) = a.ano == b.ano && a.mes == b.mes && b.dia == a.dia + 1

    fun periodoPorExtenso(inicio: String, fim: String): String {
        val a = ler(inicio) ?: return "em ${inicio.trim()}"
        val b = ler(fim) ?: a
        return when {
            ordinal(b) <= ordinal(a) -> "em ${a.dia} de ${mes(a)} de ${a.ano}"
            consecutivos(a, b) -> "nos dias ${a.dia} e ${b.dia} de ${mes(a)} de ${a.ano}"
            a.ano == b.ano && a.mes == b.mes -> "no período de ${a.dia} a ${b.dia} de ${mes(a)} de ${a.ano}"
            a.ano == b.ano -> "no período de ${a.dia} de ${mes(a)} a ${b.dia} de ${mes(b)} de ${a.ano}"
            else -> "no período de ${a.dia} de ${mes(a)} de ${a.ano} a ${b.dia} de ${mes(b)} de ${b.ano}"
        }
    }

    fun dataValida(s: String) = ler(s) != null

    fun periodoValido(inicio: String, fim: String): Boolean {
        val a = ler(inicio) ?: return false
        val b = ler(fim) ?: return false
        return ordinal(b) >= ordinal(a)
    }

    /** Extrai horas numéricas de textos como "40 horas". */
    fun horasCarga(cargaHoraria: String): Int =
        Regex("""(\d+)""").find(cargaHoraria.trim())?.groupValues?.get(1)?.toIntOrNull() ?: 0

    /** Dias de treinamento (8 h/dia, arredondado para cima). */
    fun diasTreinamento(horas: Int): Int = when {
        horas <= 0 -> 1
        else -> ceil(horas / 8.0).toInt()
    }

    /** Domingo no epoch-day (% 7 == 3); treino seg–sáb. */
    private fun epochDomingo(dias: Long): Boolean = dias % 7 == 3L

    private fun brDeEpoch(dias: Long): String = DatasColab.isoParaBr(DatasColab.isoDeDias(dias))

    private fun inicioUtil(dias: Long): Long {
        var d = dias
        while (epochDomingo(d)) d++
        return d
    }

    /** Último dia do curso contando só seg–sáb a partir de [inicioBr] (dd/MM/yyyy). */
    fun fimCursoPorCarga(inicioBr: String, horas: Int): String {
        val need = diasTreinamento(horas)
        var d = DatasColab.diasDeBr(inicioBr) ?: return inicioBr.trim()
        d = inicioUtil(d)
        var counted = 1
        while (counted < need) {
            d++
            if (!epochDomingo(d)) counted++
        }
        return brDeEpoch(d)
    }

    /** Fim do curso e data de emissão = último dia de treinamento. */
    fun periodoEEmissaoPorCarga(inicioBr: String, horas: Int): Pair<String, String> {
        val fim = fimCursoPorCarga(inicioBr, horas)
        return fim to fim
    }

    suspend fun build(dados: CertificadoDados): ByteArray = build(
        dados,
        PlatformServices.loadBundledAsset(dados.modelo.assetPagina1),
        PlatformServices.loadBundledAsset(dados.modelo.assetPagina2)
    )

    fun build(dados: CertificadoDados, fundo1: ByteArray?, fundo2: ByteArray?): ByteArray {
        val m = dados.modelo
        val pg = PdfPagina(W, H, null)
        val img1 = fundo1?.let { pg.registrarImagem(it) }
        val img2 = fundo2?.let { pg.registrarImagem(it) }
        val nome = dados.nome.trim().uppercase()

        img1?.let { pg.imagem(it, 0.0, 0.0, W, H) }

        val trechos = listOf(
            EMPRESA to true,
            ", certifica que " to false,
            nome to true,
            " portador do CPF " to false,
            "${Cpf.formatar(dados.cpf)}," to true,
            " concluiu com aproveitamento satisfatório o " to false,
            m.curso to true,
            " com carga horária de " to false,
            m.cargaHoraria to true,
            " em conformidade com a Norma Regulamentadora ${m.norma}, realizado " +
                "${periodoPorExtenso(dados.inicio, dados.fim)}." to false
        )
        paragrafo(pg, trechos, m.topoParagrafo, m.topoCidade - 6.0)

        pg.text(
            "${dados.cidade.trim()}, ${dataPorExtenso(dados.dataEmissao)}.",
            m.xCidade, H - m.topoCidade - 12.5, 14.0, false
        )

        var tam = 12.0
        val larguraNome = m.nomeXb - m.nomeXa - 6.0
        while (tam > 7.0 && PdfTexto.largura(nome, tam, true) > larguraNome) tam -= 0.5
        pg.textCentered(PdfTexto.truncar(nome, larguraNome, tam, true), m.nomeXa, m.nomeXb, H - m.baseNome, tam, true)
        if (m.desenharLinhaAluno) {
            pg.hline(m.nomeXa, m.nomeXb, H - m.baseNome + 17.0)
            pg.textCentered(ROTULO_ALUNO, m.nomeXa, m.nomeXb, H - m.baseNome - 17.0, 11.0, false)
        }

        pg.newPage()
        img2?.let { pg.imagem(it, 0.0, 0.0, W, H) }
        return pg.finish("Certificado ${m.rotulo} - $nome")
    }

    /** Palavra com trechos negrito/normal (ex.: CPF em negrito seguido de vírgula). */
    private class Palavra(val partes: List<Pair<String, Boolean>>) {
        fun largura(tam: Double) = partes.sumOf { (t, b) -> PdfTexto.largura(t, tam, b) }
    }

    private fun palavras(trechos: List<Pair<String, Boolean>>): List<Palavra> {
        val out = mutableListOf<Palavra>()
        var atual = mutableListOf<Pair<String, Boolean>>()
        for ((texto, negrito) in trechos) {
            val sb = StringBuilder()
            for (ch in texto) {
                if (ch == ' ') {
                    if (sb.isNotEmpty()) atual += sb.toString() to negrito
                    sb.clear()
                    if (atual.isNotEmpty()) out += Palavra(atual)
                    atual = mutableListOf()
                } else {
                    sb.append(ch)
                }
            }
            if (sb.isNotEmpty()) atual += sb.toString() to negrito
        }
        if (atual.isNotEmpty()) out += Palavra(atual)
        return out
    }

    private fun quebrar(ps: List<Palavra>, tam: Double): List<List<Palavra>> {
        val espaco = PdfTexto.largura(" ", tam, false)
        val linhas = mutableListOf<MutableList<Palavra>>()
        var largura = 0.0
        for (p in ps) {
            val w = p.largura(tam)
            val atual = linhas.lastOrNull()
            if (atual == null || largura + espaco + w > X1 - X0) {
                linhas += mutableListOf(p)
                largura = w
            } else {
                atual += p
                largura += espaco + w
            }
        }
        return linhas
    }

    private fun paragrafo(pg: PdfPagina, trechos: List<Pair<String, Boolean>>, topo: Double, limite: Double) {
        val ps = palavras(trechos)
        var tam = 14.0
        var linhas = quebrar(ps, tam)
        while (tam > 10.0 && topo + linhas.size * tam * 1.45 > limite) {
            tam -= 0.5
            linhas = quebrar(ps, tam)
        }
        val entre = tam * 1.45
        val espaco = PdfTexto.largura(" ", tam, false)
        linhas.forEachIndexed { i, linha ->
            val total = linha.sumOf { it.largura(tam) } + espaco * (linha.size - 1)
            var x = X0 + (X1 - X0 - total) / 2
            val base = H - topo - tam - i * entre
            linha.forEach { p ->
                p.partes.forEach { (t, b) ->
                    pg.text(t, x, base, tam, b)
                    x += PdfTexto.largura(t, tam, b)
                }
                x += espaco
            }
        }
    }
}
